import type { BlockaidValidationResult } from '@infinex/connect-service-contract/client';

export interface ProgramDetails {
  address: string;
  name: string;
  type?: string;
  was_written_to?: boolean;
}

/**
 * Get Solana transaction actions
 */
export function getSolanaActions(
  txnValidationResult: BlockaidValidationResult
): string {
  if (!txnValidationResult) return '';

  // Only handle Solana transactions
  if (
    !('result' in txnValidationResult) ||
    !txnValidationResult.result?.simulation
  ) {
    return '';
  }

  const actions = (txnValidationResult.result.simulation as any)
    .transaction_actions;
  // Use transaction_actions to get a general idea
  // Note: Solana doesn't have function names like EVM, instructions are identified by discriminators
  // Solana doesn't have transaction_actions in the simulation response type for some reason
  if (actions && Array.isArray(actions)) {
    // Join the actions to create a readable description
    // e.g., ["native_transfer", "token_transfer"] -> "Native transfer, Token transfer"
    return actions
      .map((action: string) =>
        action
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      )
      .join(', ');
  }

  return '';
}

/**
 * Extract all Solana programs from transaction validation result
 */
export function getSolanaPrograms(
  txnValidationResult: BlockaidValidationResult
): ProgramDetails[] {
  if (!txnValidationResult) return [];

  // Only handle Solana transactions
  if (
    !('result' in txnValidationResult) ||
    !txnValidationResult.result?.simulation
  ) {
    return [];
  }

  const simulation = txnValidationResult.result.simulation;
  const programs: ProgramDetails[] = [];

  if (
    simulation.accounts_details &&
    Array.isArray(simulation.accounts_details)
  ) {
    // Get all relevant accounts - Programs, PDAs, Token Accounts, and potentially native programs
    const relevantAccounts = simulation.accounts_details.filter(
      (account: any) =>
        account.type === 'PROGRAM' ||
        account.type === 'PDA' ||
        account.type === 'TOKEN_ACCOUNT' ||
        account.type === 'NATIVE_PROGRAM'
    );

    relevantAccounts.forEach((account: any) => {
      // Clean up the name by removing common prefixes
      let cleanName =
        account.description && account.description !== 'N/A'
          ? account.description
          : account.account_address;

      // Remove common repetitive prefixes to make the display cleaner
      cleanName = cleanName
        .replace(/^PDA owned by /i, '')
        .replace(/^Token account owned by /i, '')
        .replace(/^Program owned by /i, '')
        .replace(/^Account owned by /i, '');

      programs.push({
        address: account.account_address,
        name: cleanName,
        type: account.type,
        was_written_to: account.was_written_to,
      });
    });
  }

  return programs;
}
